import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Pagination from "../../../Component/Common/Pagination";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { BASE_URL } from "../../../config/api";
import { Phone, MapPin, ShieldCheck, ChevronRight, Store, Users } from "lucide-react";

import StyledTable, {
  T,
  StatusBadge,
  VerifiedBadge,
  UsageStatusBadge,
  CreatedByBadge,
  Avatar,
} from "../../../Component/Common/Styledtable";
import axios from "axios";

// ─── MOLECULE: RetailerMobileCard ─────────────────────────────────────────────
function RetailerMobileCard({ r, authUserId, onView }) {
  return (
    <div
      className={`rounded-2xl border overflow-hidden transition-all duration-200 hover:shadow-md active:scale-[0.99] ${
          Number(r.is_subscribed) === 1
            ? "bg-green-50 border-green-200"
            : "bg-white border-slate-100"
        }`}
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
    >
      <div className="h-1 w-full" style={{ background: r.status === 1 ? T.success : T.danger }} />
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <Avatar name={r.business_name} size={10} />
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-bold text-slate-800 leading-tight truncate">
              {r.business_name}
            </p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <p
                className="text-[12px] font-black px-1.5 py-0.5 rounded inline-block"
                style={{ background: T.brandSoft, color: T.brand }}
              >
                {r.company_code}
              </p>
              <StatusBadge active={r.status === 1} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="flex items-center gap-1.5">
            <MapPin size={11} style={{ color: T.slate400 }} />
            <p className="text-[12px] text-slate-600 truncate">
              {r.district}, {r.state}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Phone size={11} style={{ color: T.brand }} />
            <a
              href={`tel:${r.contact_phone}`}
              onClick={e => e.stopPropagation()}
              className="text-[12px] font-semibold truncate"
              style={{ color: T.brand }}
            >
              {r.contact_phone}
            </a>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 flex-wrap">
            <VerifiedBadge verified={r.is_verified === 7} />
            <CreatedByBadge
              createdById={r.created_by_id}
              agentId={r.agent_id}
              createdByName={r.created_by_name}
              authUserId={authUserId}
            />
          </div>
          <button
            onClick={e => { e.stopPropagation(); onView(r); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-[12px] font-black text-white transition-all hover:scale-105 active:scale-95"
            style={{
              background:  `linear-gradient(135deg,${T.brand},${T.brandDark})`,
              boxShadow:   `0 3px 10px ${T.brand}44`,
            }}
          >
            View <ChevronRight size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MOLECULE: DesktopTableRow ────────────────────────────────────────────────
function DesktopTableRow({ r, authUserId, onView, index }) {
  return (
    <tr
  className={`group border-b border-slate-50 transition-colors cursor-pointer ${
    Number(r.is_subscribed) === 1
      ? "bg-green-50 hover:bg-green-100/60"
      : "hover:bg-blue-50/40"
  }`}>
      {/* Business */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <Avatar name={r.business_name} size={8} />
          <div className="min-w-0">
            <p className="text-[12px] font-bold text-slate-800 leading-tight">{r.business_name}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <p className="text-[12px] font-black" style={{ color: T.brand }}>{r.company_code}</p>
              <StatusBadge active={r.status === 1} />
            </div>
          </div>
        </div>
      </td>

      {/* Location */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-1.5">
          <MapPin size={11} style={{ color: T.slate400 }} />
          <div>
            <p className="text-[12px] text-slate-700 font-medium">{r.district}</p>
            <p className="text-[12px] text-slate-400">{r.state} · {r.pincode || "—"}</p>
          </div>
        </div>
      </td>

      {/* Usage Status */}
      <td className="py-3 px-4">
        <UsageStatusBadge flag={r.flag} />
      </td>

      {/* Verified */}
      <td className="py-3 px-4">
        <VerifiedBadge verified={r.is_verified === 7} />
      </td>

      {/* Phone */}
      <td className="py-3 px-4">
        <a
          href={`tel:${r.contact_phone}`}
          onClick={e => e.stopPropagation()}
          className="flex items-center gap-1.5 text-[12px] font-semibold hover:opacity-80 transition-opacity"
          style={{ color: T.brand }}
        >
          <Phone size={11} /> {r.contact_phone}
        </a>
      </td>

      {/* Created by */}
      <td className="py-3 px-4">
        <CreatedByBadge
          createdById={r.created_by_id}
          agentId={r.agent_id}
          createdByName={r.created_by_name}
          authUserId={authUserId}
        />
      </td>

      {/* Action */}
      <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
        <button
          onClick={() => onView(r)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-black text-white transition-all hover:scale-105 active:scale-95"
          style={{
            background:  `linear-gradient(135deg,${T.brand},${T.brandDark})`,
            boxShadow:   `0 3px 10px ${T.brand}33`,
          }}
        >
          View <ChevronRight size={11} />
        </button>
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ROOT: OprRetailersView
// ─────────────────────────────────────────────────────────────────────────────
export default function OprRetailersView() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const filterType = location.state?.filter || null;

  const [activeTab, setActiveTab] = useState("ARP");
  const [usageFilter, setUsageFilter] = useState("");


  const authUser     = localStorage.getItem("authUserData") ? JSON.parse(localStorage.getItem("authUserData")) : null;
  const companyId    = authUser?.company_id;
  const locationMode = authUser?.location_mode;
  const state        = authUser?.state;
  const district     = authUser?.district;
  const showTabs     =
    authUser?.role === 3 &&
    Array.isArray(authUser?.handle) &&
    authUser.handle.includes("AGENT") &&
    authUser.handle.includes("RETAILER");

  const [retailers,   setRetailers]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchField, setSearchField] = useState("business_name");
  const [searchValue, setSearchValue] = useState("");
  const [pagination,  setPagination]  = useState({ current_page: 1, last_page: 1, total: 0 });

  const [subscribedCount, setSubscribedCount] = useState(0);  

  const WARRANTY_API_MAP = {
    assigned:  "warranty/assigned-retailers",
    connected: "warranty/connected-retailers",
    using:     "warranty/using-retailers",
  };

  const fetchMainRetailers = async () => {

    const flagMap = {
      "Using": "working",
      "Not Using": "inactive",
      "ACT-PD": "activation_pending",
      "In Progress": "in_progress",
    };

    const roleParam =
      showTabs &&
      activeTab === "CP"
        ? 4
        : 5;

    let url =
      `${BASE_URL}company/list?company_id=${companyId}&role=${roleParam}&page=${currentPage}`;

    if (
      locationMode === "state" &&
      state
    ) {

      url += `&state=${encodeURIComponent(state)}`;
    }

    if (
      locationMode === "district" &&
      district
    ) {

      url += `&district=${encodeURIComponent(district)}`;
    }

    if (
      searchValue.trim()
    ) {

      url += `&${searchField}=${encodeURIComponent(
        searchValue.trim()
      )}`;
    }

    if (usageFilter) {

      url += `&flag=${encodeURIComponent(
        flagMap[usageFilter] || usageFilter
      )}`;
    }

    try {

      const response =
        await axios.get(
          url,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const json =
        response.data;

      if (json?.status) {

        setPagination(
          json.pagination || {}
        );

        return (
          json.data || []
        );
      }

      return [];

    } catch (err) {

      console.error(
        "Fetch retailers error:",
        err
      );

      return [];
    }
  };

  const fetchWarrantyRetailers = async () => {

    const params = {};

    if (
      locationMode === "state" &&
      state
    ) {

      params.states =
        state;
    }

    if (
      locationMode === "district" &&
      district
    ) {

      params.district =
        district;
    }

    try {

      const response =
        await axios.get(
          `${BASE_URL}${WARRANTY_API_MAP[filterType]}`,
          {
            params,

            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const json =
        response.data;

      return json?.success
        ? json.data || []
        : [];

    } catch (err) {

      console.error(
        "Fetch warranty retailers error:",
        err
      );

      return [];
    }
  };

  const mergeRetailersById = (mainList, filterList) => {
    const filterMap = new Map(filterList.map(r => [r.id, r]));
    return mainList.filter(r => filterMap.has(r.id)).map(r => ({ ...r, ...filterMap.get(r.id) }));
  };

  const fetchRetailers = async () => {
    try {
      setLoading(true);
      if (filterType && WARRANTY_API_MAP[filterType] && activeTab === "ARP") {
        const [main, warranty] = await Promise.all([fetchMainRetailers(), fetchWarrantyRetailers()]);
        const mergedData = mergeRetailersById(main, warranty);

          setRetailers(mergedData);

          const subscribedUsers = mergedData.filter(
            (item) => Number(item.is_subscribed) === 1
          );

          setSubscribedCount(subscribedUsers.length);
        return;
      }
      const retailerData = await fetchMainRetailers();

      setRetailers(retailerData);

      const subscribedUsers = retailerData.filter(
        (item) => Number(item.is_subscribed) === 1
      );

      setSubscribedCount(subscribedUsers.length);
    } catch (err) {
      console.error("Failed to fetch retailers", err);
      setRetailers([]);
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  fetchRetailers();
}, [
  filterType,
  activeTab,
  currentPage,
  usageFilter,
]);

 useEffect(() => {
  const t = setTimeout(fetchRetailers, 400);

  return () => clearTimeout(t);
}, [
  searchValue,
  searchField,
  activeTab,
  usageFilter,
]);

  const handleView = (r) =>
    navigate(`/user/${r.id}`, {
      state: { entityType: activeTab === "CP" ? "agent" : "retailer", data: r },
    });

  const SEARCH_OPTIONS = [
    { value: "business_name", label: "Business Name" },
    { value: "company_code",  label: "Code"          },
    { value: "city",          label: "City"          },
    { value: "state",         label: "State"         },
    { value: "district",      label: "District"      },
    { value: "pincode",       label: "Pincode"       },
  ];

  const tabLabel = showTabs && activeTab === "CP" ? "CP" : "ARP";

  return (
    <div className="space-y-4" style={{ fontFamily: "'Plus Jakarta Sans','DM Sans',sans-serif" }}>

      {/* ── TAB BAR ── */}
      {showTabs && (
        <div className="flex items-center gap-2">
          {[
            { id: "ARP", icon: Store,  label: "ARP"              },
            { id: "CP",  icon: Users,  label: "Channel Partners" },
          ].map(tab => {
            const active = activeTab === tab.id;
            const Icon   = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-black transition-all duration-150"
                style={{
                  background: active ? T.brand  : "#fff",
                  color:      active ? "#fff"    : T.slate600,
                  border:     `1.5px solid ${active ? T.brand : "#E2E8F0"}`,
                  boxShadow:  active ? `0 4px 14px ${T.brand}33` : "none",
                }}
              >
                <Icon size={13} /> {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ── STYLED TABLE ── */}
      <StyledTable
        title={`${tabLabel} Directory`}
        icon={Store}
        total={pagination.total}
        subscribedCount={subscribedCount}
        loading={loading}
        emptyLabel={tabLabel}
        headers={["Business", "Location", "Usage Status", "Verified", "Phone", "Created By", ""]}
        items={retailers}
        searchField={searchField}
        searchOptions={SEARCH_OPTIONS}
        searchValue={searchValue}
        onSearchFieldChange={f => { setSearchField(f); setCurrentPage(1); }}
        onSearchChange={v => { setSearchValue(v); setCurrentPage(1); }}
        onRefresh={fetchRetailers}
        currentPage={currentPage}
        totalPages={pagination.last_page}
        onPageChange={setCurrentPage}
        usageFilter={usageFilter}
        onUsageFilterChange={(value) => {
          setUsageFilter(value);
          setCurrentPage(1);
        }}
        renderRow={(r, i) => (
          <DesktopTableRow
            key={r.id}
            r={r}
            index={i}
            authUserId={authUser?.id}
            onView={handleView}
          />
        )}
        renderCard={(r) => (
          <RetailerMobileCard
            key={r.id}
            r={r}
            authUserId={authUser?.id}
            onView={handleView}
          />
        )}
      />
    </div>
  );
}


// import { useLocation, useNavigate } from "react-router-dom";
// import { useState, useMemo, useEffect } from "react";
// import { Badge } from "../../../Component/OperationComponents/Badge";
// import { SectionCard } from "../../../Component/OperationComponents/SectionCard";
// import Pagination from "../../../Component/Common/Pagination";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { BASE_URL } from "../../../config/api";
// import {
//   Search, Phone, MapPin, ShieldCheck, User,
//   ChevronRight, Building2, RefreshCw, SlidersHorizontal,
//   Users, Store,
// } from "lucide-react";

// // ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
// const T = {
//   brand:    "#2563EB",
//   brandDark:"#1D4ED8",
//   brandSoft:"#EFF6FF",
//   brandBorder:"#BFDBFE",
//   success:  "#10B981",
//   successSoft:"#ECFDF5",
//   danger:   "#EF4444",
//   dangerSoft:"#FEF2F2",
//   slate800: "#1E293B",
//   slate600: "#475569",
//   slate400: "#94A3B8",
//   slate100: "#F1F5F9",
//   slate50:  "#F8FAFC",
// };

// // ─── ATOM: StatusBadge ───────────────────────────────────────────────────────
// function StatusBadge({ active }) {
//   return (
//     <span
//       className="inline-flex items-center gap-1 text-[12px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
//       style={{
//         background: active ? T.successSoft : T.dangerSoft,
//         color:      active ? T.success     : T.danger,
//         border:     `1px solid ${active ? "#A7F3D0" : "#FECACA"}`,
//       }}
//     >
//       <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? T.success : T.danger }}/>
//       {active ? "Active" : "Inactive"}
//     </span>
//   );
// }

// // ─── ATOM: VerifiedBadge ─────────────────────────────────────────────────────
// function VerifiedBadge({ verified }) {
//   return (
//     <span
//       className="inline-flex items-center gap-1 text-[12px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
//       style={{
//         background: verified ? "#EFF6FF" : "#F8FAFC",
//         color:      verified ? T.brand    : T.slate400,
//         border:     `1px solid ${verified ? T.brandBorder : "#E2E8F0"}`,
//       }}
//     >
//       <ShieldCheck size={9}/>
//       {verified ? "Onboarded" : "Pending"}
//     </span>
//   );
// }

// // ─── ATOM: CreatedByBadge ────────────────────────────────────────────────────
// function CreatedByBadge({ r, authUserId }) {
//   const isSelf  = r.created_by_id === authUserId;
//   const isAgent = !isSelf && r.agent_id !== null;
//   const label   = isSelf ? "Self" : isAgent ? "By Agent" : "Other";
//   const color   = isSelf ? T.success : isAgent ? T.brand : T.slate400;
//   const bg      = isSelf ? T.successSoft : isAgent ? T.brandSoft : T.slate50;
//   return (
//     <div>
//       <span
//         className="inline-flex items-center gap-1 text-[12px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
//         style={{ background: bg, color, border: `1px solid ${isSelf ? "#A7F3D0" : isAgent ? T.brandBorder : "#E2E8F0"}` }}
//       >
//         {label}
//       </span>
//       {r.created_by_name && (
//         <p className="text-[12px] text-slate-400 mt-0.5">{r.created_by_name}</p>
//       )}
//     </div>
//   );
// }

// function UsageStatusBadge({ flag }) {
//   let label = "Unknown";
//   let bg = "#F1F5F9";
//   let color = "#64748B";
//   let border = "#CBD5E1";

//   if (flag === "working") {
//     label = "Using";
//     bg = "#ECFDF5";
//     color = "#10B981";
//     border = "#A7F3D0";
//   }

//   if (flag === "inactive") {
//     label = "Not Using";
//     bg = "#FEFCE8";
//     color = "#CA8A04";
//     border = "#FDE68A";
//   }

//   if (flag === "activation_pending") {
//     label = "Not Selling";
//     bg = "#EFF6FF";
//     color = "#2563EB";
//     border = "#BFDBFE";
//   }

//   return (
//     <span
//       className="inline-flex items-center gap-1 text-[12px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
//       style={{
//         background: bg,
//         color,
//         border: `1px solid ${border}`,
//       }}
//     >
//       <span
//         className="w-1.5 h-1.5 rounded-full"
//         style={{ background: color }}
//       />
//       {label}
//     </span>
//   );
// }

// // ─── MOLECULE: RetailerMobileCard ─────────────────────────────────────────────
// function RetailerMobileCard({ r, authUserId, onView }) {
//   const initials = (r.business_name || "R").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
//   return (
//     <div
//       className="bg-white rounded-2xl border border-slate-100 overflow-hidden transition-all duration-200 hover:shadow-md active:scale-[0.99]"
//       style={{ boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}
//     >
//       {/* Status strip */}
//       <div
//         className="h-1 w-full"
//         style={{ background: r.status === 1 ? T.success : T.danger }}
//       />

//       <div className="p-4">
//         {/* Header row */}
//         <div className="flex items-start gap-3 mb-3">
//           {/* Avatar */}
//           <div
//             className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-white text-[12px] shrink-0"
//             style={{ background:`linear-gradient(135deg,${T.brand},${T.brandDark})` }}
//           >
//             {initials}
//           </div>
//           <div className="flex-1 min-w-0">
//             <p className="text-[12px] font-bold text-slate-800 leading-tight truncate">
//               {r.business_name}
//             </p>
//             <div className="flex items-center gap-2 mt-1 flex-wrap">
//               <p
//                 className="text-[12px] font-black px-1.5 py-0.5 rounded inline-block"
//                 style={{ background:T.brandSoft, color:T.brand }}
//               >
//                 {r.company_code}
//               </p>

//               <StatusBadge active={r.status === 1}/>
//             </div>
//           </div>
//         </div>

//         {/* Info grid */}
//         <div className="grid grid-cols-2 gap-2 mb-3">
//           <div className="flex items-center gap-1.5">
//             <MapPin size={11} style={{ color:T.slate400 }}/>
//             <p className="text-[12px] text-slate-600 truncate">
//               {r.district}, {r.state}
//             </p>
//           </div>
//           <div className="flex items-center gap-1.5">
//             <Phone size={11} style={{ color:T.brand }}/>
//             <a
//               href={`tel:${r.contact_phone}`}
//               onClick={e => e.stopPropagation()}
//               className="text-[12px] font-semibold truncate"
//               style={{ color:T.brand }}
//             >
//               {r.contact_phone}
//             </a>
//           </div>
//         </div>

//         {/* Tags row */}
//         <div className="flex items-center justify-between">
//           <div className="flex items-center gap-1.5 flex-wrap">
//             <VerifiedBadge verified={r.is_verified === 7}/>
//             <CreatedByBadge r={r} authUserId={authUserId}/>
//           </div>
//           <button
//             onClick={e => { e.stopPropagation(); onView(r); }}
//             className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-[12px] font-black text-white transition-all hover:scale-105 active:scale-95"
//             style={{ background:`linear-gradient(135deg,${T.brand},${T.brandDark})`, boxShadow:`0 3px 10px ${T.brand}44` }}
//           >
//             View <ChevronRight size={11}/>
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// // ─── MOLECULE: DesktopTableRow ────────────────────────────────────────────────
// function DesktopTableRow({ r, authUserId, activeTab, onView, index }) {
//   const initials = (r.business_name || "R").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
//   return (
//     <tr
//       className="group border-b border-slate-50 hover:bg-blue-50/40 transition-colors cursor-pointer"
//       style={{ animationDelay:`${index*0.03}s` }}
//     >
//       {/* Business */}
//       <td className="py-3 px-4">
//         <div className="flex items-center gap-3">
//           <div
//             className="w-8 h-8 rounded-xl flex items-center justify-center font-black text-white text-[12px] shrink-0"
//             style={{ background:`linear-gradient(135deg,${T.brand},${T.brandDark})` }}
//           >
//             {initials}
//           </div>
//           <div className="min-w-0">
//             <p className="text-[12px] font-bold text-slate-800 leading-tight">
//               {r.business_name}
//             </p>
//             <div className="flex items-center gap-2 mt-1 flex-wrap">
//               <p
//                 className="text-[12px] font-black"
//                 style={{ color:T.brand }}
//               >
//                 {r.company_code}
//               </p>

//               <StatusBadge active={r.status === 1}/>
//             </div>
//           </div>
//         </div>
//       </td>

//       {/* Location */}
//       <td className="py-3 px-4">
//         <div className="flex items-center gap-1.5">
//           <MapPin size={11} style={{ color:T.slate400 }}/>
//           <div>
//             <p className="text-[12px] text-slate-700 font-medium">{r.district}</p>
//             <p className="text-[12px] text-slate-400">{r.state} · {r.pincode || "—"}</p>
//           </div>
//         </div>
//       </td>

//       {/* Status */}
//       <td className="py-3 px-4">
//         <UsageStatusBadge flag={r.flag} />
//       </td>

//       {/* Verified */}
//       <td className="py-3 px-4">
//         <VerifiedBadge verified={r.is_verified === 7}/>
//       </td>

//       {/* Phone */}
//       <td className="py-3 px-4">
//         <a
//           href={`tel:${r.contact_phone}`}
//           onClick={e => e.stopPropagation()}
//           className="flex items-center gap-1.5 text-[12px] font-semibold hover:opacity-80 transition-opacity"
//           style={{ color:T.brand }}
//         >
//           <Phone size={11}/> {r.contact_phone}
//         </a>
//       </td>

//       {/* Created by */}
//       <td className="py-3 px-4">
//         <CreatedByBadge r={r} authUserId={authUserId}/>
//       </td>

//       {/* Action */}
//       <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
//         <button
//           onClick={() => onView(r)}
//           className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-black text-white transition-all hover:scale-105 active:scale-95"
//           style={{ background:`linear-gradient(135deg,${T.brand},${T.brandDark})`, boxShadow:`0 3px 10px ${T.brand}33` }}
//         >
//           View <ChevronRight size={11}/>
//         </button>
//       </td>
//     </tr>
//   );
// }

// // ─── MOLECULE: EmptyState ────────────────────────────────────────────────────
// function EmptyState({ label }) {
//   return (
//     <div className="flex flex-col items-center justify-center py-16 text-center">
//       <div
//         className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
//         style={{ background:T.brandSoft }}
//       >
//         <Store size={24} style={{ color:T.brand, opacity:0.5 }}/>
//       </div>
//       <p className="text-[14px] font-bold text-slate-700">No {label} found</p>
//       <p className="text-[12px] text-slate-400 mt-1">Try changing your search or filter criteria</p>
//     </div>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  ROOT: OprRetailersView  — SAME LOGIC, redesigned UI
// // ─────────────────────────────────────────────────────────────────────────────
// export default function OprRetailersView() {
//   const navigate   = useNavigate();
//   const location   = useLocation();
//   const filterType = location.state?.filter || null;

//   const [activeTab, setActiveTab] = useState("ARP");

//   /* ── Session user ── */
//   const authUser   = localStorage.getItem("authUserData") ? JSON.parse(localStorage.getItem("authUserData")) : null;
//   const companyId  = authUser?.company_id;
//   const locationMode = authUser?.location_mode;
//   const state      = authUser?.state;
//   const district   = authUser?.district;
//   const showTabs   = authUser?.role === 3 && Array.isArray(authUser?.handle) &&
//     authUser.handle.includes("AGENT") && authUser.handle.includes("RETAILER");

//   /* ── States ── */
//   const [retailers,          setRetailers]          = useState([]);
//   const [loading,            setLoading]            = useState(false);
//   const [selectedRetailerId, setSelectedRetailerId] = useState(null);
//   const [currentPage,        setCurrentPage]        = useState(1);
//   const [searchField,        setSearchField]        = useState("business_name");
//   const [searchValue,        setSearchValue]        = useState("");
//   const [pagination,         setPagination]         = useState({ current_page:1, last_page:1, total:0 });

//   /* ── Same logic as original ── */
//   const buildLocationParams = () => {
//     const params = {};
//     if (locationMode === "state"    && state)    params.states   = state;
//     if (locationMode === "district" && district) params.district = district;
//     return params;
//   };

//   const WARRANTY_API_MAP = {
//     assigned:  "warranty/assigned-retailers",
//     connected: "warranty/connected-retailers",
//     using:     "warranty/using-retailers",
//   };

//   const fetchMainRetailers = async () => {
//     const roleParam = showTabs && activeTab === "CP" ? 4 : 5;
//     let url = `${BASE_URL}company/list?company_id=${companyId}&role=${roleParam}&page=${currentPage}`;
//     if (locationMode === "state"    && state)    url += `&state=${encodeURIComponent(state)}`;
//     if (locationMode === "district" && district) url += `&district=${encodeURIComponent(district)}`;
//     if (searchValue.trim()) url += `&${searchField}=${encodeURIComponent(searchValue.trim())}`;
//     const res = await fetch(url);
//     const json = await res.json();
//     if (json?.status) { setPagination(json.pagination || {}); return json.data || []; }
//     return [];
//   };

//   const fetchWarrantyRetailers = async () => {
//     const params = buildLocationParams();
//     const res  = await fetch(`${BASE_URL}${WARRANTY_API_MAP[filterType]}?${new URLSearchParams(params)}`);
//     const json = await res.json();
//     return json?.success ? json.data || [] : [];
//   };

//   const mergeRetailersById = (mainList, filterList) => {
//     const filterMap = new Map(filterList.map(r => [r.id, r]));
//     return mainList.filter(r => filterMap.has(r.id)).map(r => ({ ...r, ...filterMap.get(r.id) }));
//   };

//   const fetchRetailers = async () => {
//     try {
//       setLoading(true);
//       if (filterType && WARRANTY_API_MAP[filterType] && activeTab === "ARP") {
//         const [main, warranty] = await Promise.all([fetchMainRetailers(), fetchWarrantyRetailers()]);
//         setRetailers(mergeRetailersById(main, warranty));
//         return;
//       }
//       setRetailers(await fetchMainRetailers());
//     } catch (err) {
//       console.error("Failed to fetch retailers", err);
//       setRetailers([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => { fetchRetailers(); }, [filterType, activeTab, currentPage]);
//   useEffect(() => {
//     const t = setTimeout(fetchRetailers, 400);
//     return () => clearTimeout(t);
//     setCurrentPage(1);
//   }, [searchValue, searchField, activeTab]);

//   /* ── Navigate helper ── */
//   const handleView = (r) => navigate(`/user/${r.id}`, {
//     state: { entityType: activeTab === "CP" ? "agent" : "retailer", data: r }
//   });

//   const SEARCH_OPTIONS = [
//     { value:"business_name", label:"Business Name" },
//     { value:"company_code",  label:"Code"          },
//     { value:"city",          label:"City"          },
//     { value:"state",         label:"State"         },
//     { value:"district",      label:"District"      },
//     { value:"pincode",       label:"Pincode"       },
//   ];

//   const tabLabel = showTabs && activeTab === "CP" ? "CP" : "ARP";

//   return (
//     <div className="space-y-4" style={{ fontFamily:"'Plus Jakarta Sans','DM Sans',sans-serif" }}>

//       {/* ── TAB BAR ── */}
//       {showTabs && (
//         <div className="flex items-center gap-2">
//           {[
//             { id:"ARP", icon:Store,  label:"ARP" },
//             { id:"CP",  icon:Users, label:"Channel Partners" },
//           ].map(tab => {
//             const active = activeTab === tab.id;
//             const Icon   = tab.icon;
//             return (
//               <button
//                 key={tab.id}
//                 onClick={() => setActiveTab(tab.id)}
//                 className="flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-black transition-all duration-150"
//                 style={{
//                   background:  active ? T.brand    : "#fff",
//                   color:       active ? "#fff"      : T.slate600,
//                   border:      `1.5px solid ${active ? T.brand : "#E2E8F0"}`,
//                   boxShadow:   active ? `0 4px 14px ${T.brand}33` : "none",
//                 }}
//               >
//                 <Icon size={13}/> {tab.label}
//               </button>
//             );
//           })}
//         </div>
//       )}

//       {/* ── MAIN CARD ── */}
//       <div
//         className="bg-white rounded-2xl overflow-hidden"
//         style={{ border:`1px solid #E2E8F0`, boxShadow:"0 2px 16px rgba(0,0,0,0.06)" }}
//       >

//         {/* ── CARD HEADER ── */}
//         <div
//           className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b"
//           style={{ borderColor:"#F1F5F9" }}
//         >
//           {/* Title */}
//           <div className="flex items-center gap-2.5">
//             <div className="w-8 h-8 rounded-xl flex items-center justify-center"
//               style={{ background:T.brandSoft }}>
//               <Store size={15} style={{ color:T.brand }}/>
//             </div>
//             <div>
//               <h2 className="text-[14px] font-black text-slate-800">{tabLabel} Directory</h2>
//               {pagination.total > 0 && (
//                 <p className="text-[12px] text-slate-400 font-medium">{pagination.total} records</p>
//               )}
//             </div>
//           </div>

//           {/* Controls */}
//           <div className="flex items-center gap-2 flex-wrap justify-end">
//             {/* Search type select */}
//             <div className="relative">
//               <SlidersHorizontal size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
//               <select
//                 value={searchField}
//                 onChange={e => setSearchField(e.target.value)}
//                 className="text-[12px] font-semibold pl-8 pr-3 py-2 rounded-xl border border-slate-200 bg-white outline-none focus:border-blue-400 appearance-none cursor-pointer"
//                 style={{ color:T.slate600 }}
//               >
//                 {SEARCH_OPTIONS.map(o => (
//                   <option key={o.value} value={o.value}>{o.label}</option>
//                 ))}
//               </select>
//             </div>

//             {/* Search input */}
//             <div className="relative">
//               <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
//               <input
//                 type="text"
//                 placeholder={`Search by ${searchField.replace("_"," ")}…`}
//                 value={searchValue}
//                 onChange={e => setSearchValue(e.target.value)}
//                 className="text-[12px] font-semibold pl-9 pr-3 py-2 rounded-xl border border-slate-200 outline-none w-44 sm:w-52 transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
//                 style={{ color:T.slate800 }}
//               />
//             </div>

//             {/* Refresh */}
//             <button
//               onClick={fetchRetailers}
//               className="w-9 h-9 rounded-xl flex items-center justify-center border border-slate-200 hover:bg-slate-50 hover:border-blue-300 transition-all"
//             >
//               <RefreshCw size={14} style={{ color:T.slate400 }}/>
//             </button>
//           </div>
//         </div>

//         {/* ── BODY ── */}
//         <div className="p-4">
//           {loading ? (
//             <div className="flex items-center justify-center py-16">
//               <RegularLoader/>
//             </div>
//           ) : retailers.length === 0 ? (
//             <EmptyState label={tabLabel}/>
//           ) : (
//             <>
//               {/* ── MOBILE CARDS ── */}
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:hidden">
//                 {retailers.map(r => (
//                   <RetailerMobileCard
//                     key={r.id}
//                     r={r}
//                     authUserId={authUser?.id}
//                     onView={handleView}
//                   />
//                 ))}
//               </div>

//               {/* ── DESKTOP TABLE ── */}
//               <div className="hidden lg:block overflow-x-auto rounded-xl" style={{ border:"1px solid #F1F5F9" }}>
//                 <table className="min-w-full" style={{ borderCollapse:"collapse" }}>
//                   <thead>
//                     <tr style={{ background:T.slate50, borderBottom:`1px solid #E2E8F0` }}>
//                       {["Business","Location","Usage Status","Verified","Phone","Created By",""].map((h,i) => (
//                         <th
//                           key={h+i}
//                           className="py-3 px-4 text-left"
//                           style={{ fontSize:10, fontWeight:900, textTransform:"uppercase", letterSpacing:"0.1em", color:T.slate400 }}
//                         >
//                           {h}
//                         </th>
//                       ))}
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {retailers.map((r, i) => (
//                       <DesktopTableRow
//                         key={r.id}
//                         r={r}
//                         index={i}
//                         authUserId={authUser?.id}
//                         activeTab={activeTab}
//                         onView={handleView}
//                       />
//                     ))}
//                   </tbody>
//                 </table>
//               </div>

//               {/* ── PAGINATION ── */}
//               {pagination.last_page > 1 && (
//                 <div className="mt-4 flex justify-center">
//                   <Pagination
//                     currentPage={currentPage}
//                     totalPages={pagination.last_page}
//                     onPageChange={setCurrentPage}
//                   />
//                 </div>
//               )}
//             </>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }


// import { useLocation, useNavigate } from "react-router-dom";
// import { useState, useMemo, useEffect } from "react";
// import { Badge } from "../../../Component/OperationComponents/Badge";
// import { SectionCard } from "../../../Component/OperationComponents/SectionCard";
// import Pagination from "../../../Component/Common/Pagination";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { BASE_URL } from "../../../config/api";

// export default function OprRetailersView() {
//   const navigate = useNavigate();
//   const [activeTab, setActiveTab] = useState("ARP");
//   const location = useLocation();
//   const filterType = location.state?.filter || null;

//   /* ---------------- SESSION USER ---------------- */
//   const authUser = localStorage.getItem("authUserData")
//     ? JSON.parse(localStorage.getItem("authUserData"))
//     : null;

//   const companyId = authUser?.company_id;
//   const locationMode = authUser?.location_mode;
//   const state = authUser?.state;
//   const district = authUser?.district;

//   const showTabs =
//   authUser?.role === 3 &&
//   Array.isArray(authUser?.handle) &&
//   authUser.handle.includes("AGENT") &&
//   authUser.handle.includes("RETAILER");

//   /* ---------------- STATES ---------------- */
//   const [retailers, setRetailers] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [searchRetailer, setSearchRetailer] = useState("");
//   const [selectedRetailerId, setSelectedRetailerId] = useState(null);

//   /* ---------------- PAGINATION ---------------- */
//   const [currentPage, setCurrentPage] = useState(1);
//   const [searchField, setSearchField] = useState("business_name");
//   const [searchValue, setSearchValue] = useState("");

//   const [pagination, setPagination] = useState({
//     current_page: 1,
//     last_page: 1,
//     total: 0,
//   });

//   // const pageSize = 10;

//   useEffect(() => {
//     setCurrentPage(1);
//   }, [searchRetailer]);

//   const buildLocationParams = () => {
//   const params = {};

//   if (locationMode === "state" && state) {
//     params.states = state;
//   }

//   if (locationMode === "district" && district) {
//     params.district = district;
//   }

//   return params;
// };


// const WARRANTY_API_MAP = {
//   assigned: "warranty/assigned-retailers",
//   connected: "warranty/connected-retailers",
//   using: "warranty/using-retailers",
// };


//   /* ---------------- FETCH RETAILERS ---------------- */
//   // const fetchRetailers = async () => {
//   //   try {
//   //     setLoading(true);

//   //     const roleParam =
//   //     showTabs && activeTab === "agent" ? 4 : 5;


//   //     let url = `${BASE_URL}company/list?company_id=${companyId}&role=${roleParam}`;

//   //     if (locationMode === "state" && state) {
//   //       url += `&state=${encodeURIComponent(state)}`;
//   //     }

//   //     if (locationMode === "district" && district) {
//   //       url += `&district=${encodeURIComponent(district)}`;
//   //     }

//   //     const res = await fetch(url);
//   //     const json = await res.json();

//   //     if (json?.status) {
//   //       setRetailers(json.data || []);
//   //     } else {
//   //       setRetailers([]);
//   //     }
//   //   } catch (err) {
//   //     console.error("Failed to fetch retailers", err);
//   //     setRetailers([]);
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };


//   // const fetchRetailers = async () => {
//   //   try {
//   //     setLoading(true);

//   //     const roleParam =
//   //       showTabs && activeTab === "agent" ? 4 : 5;

//   //     let url = `${BASE_URL}company/list?company_id=${companyId}&role=${roleParam}`;

//   //     // 🔹 LOCATION FILTER
//   //     if (locationMode === "state" && state) {
//   //       url += `&state=${encodeURIComponent(state)}`;
//   //     }

//   //     if (locationMode === "district" && district) {
//   //       url += `&district=${encodeURIComponent(district)}`;
//   //     }

//   //     // 🔹 SEARCH FILTER (only if input exists)
//   //     if (searchValue.trim()) {
//   //       url += `&${searchField}=${encodeURIComponent(searchValue.trim())}`;
//   //     }

//   //     const res = await fetch(url);
//   //     const json = await res.json();

//   //     if (json?.status) {
//   //       setRetailers(json.data || []);
//   //     } else {
//   //       setRetailers([]);
//   //     }
//   //   } catch (err) {
//   //     console.error("Failed to fetch retailers", err);
//   //     setRetailers([]);
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const fetchMainRetailers = async () => {
//   const roleParam = showTabs && activeTab === "CP" ? 4 : 5;

//   let url = `${BASE_URL}company/list?company_id=${companyId}&role=${roleParam}&page=${currentPage}`;

//   if (locationMode === "state" && state) {
//     url += `&state=${encodeURIComponent(state)}`;
//   }

//   if (locationMode === "district" && district) {
//     url += `&district=${encodeURIComponent(district)}`;
//   }

//   if (searchValue.trim()) {
//     url += `&${searchField}=${encodeURIComponent(searchValue.trim())}`;
//   }

//   const res = await fetch(url);
//   const json = await res.json();

//   if (json?.status) {
//     setPagination(json.pagination || {});
//     return json.data || [];
//   }

//   return [];
// };

// const fetchWarrantyRetailers = async () => {
//   const params = buildLocationParams();

//   const res = await fetch(
//     `${BASE_URL}${WARRANTY_API_MAP[filterType]}?${new URLSearchParams(params)}`
//   );

//   const json = await res.json();
//   return json?.success ? json.data || [] : [];
// };


// const mergeRetailersById = (mainList, filterList) => {
//   const filterMap = new Map(filterList.map(r => [r.id, r]));

//   return mainList
//     .filter(r => filterMap.has(r.id))   // keep only matched retailers
//     .map(r => ({
//       ...r,                            // full main API data
//       ...filterMap.get(r.id),          // overlay warranty fields
//     }));
// };



//   const fetchRetailers = async () => {
//   try {
//     setLoading(true);

//     // 🔹 KPI FILTER FLOW
//     if (
//       filterType &&
//       WARRANTY_API_MAP[filterType] &&
//       activeTab === "ARP"
//     ) {
//       const [mainRetailers, warrantyRetailers] = await Promise.all([
//         fetchMainRetailers(),
//         fetchWarrantyRetailers(),
//       ]);

//       const merged = mergeRetailersById(
//         mainRetailers,
//         warrantyRetailers
//       );

//       setRetailers(merged);
//       return;
//     }

//     // 🔹 NORMAL FLOW
//     const mainRetailers = await fetchMainRetailers();
//     setRetailers(mainRetailers);
//   } catch (err) {
//     console.error("Failed to fetch retailers", err);
//     setRetailers([]);
//   } finally {
//     setLoading(false);
//   }
// };



// useEffect(() => {
//   fetchRetailers();
// }, [filterType, activeTab, currentPage]);

// useEffect(() => {
//   const t = setTimeout(fetchRetailers, 400);
//   return () => clearTimeout(t);
//   setCurrentPage(1);
// }, [searchValue, searchField, activeTab]);


//   /* ---------------- SEARCH ---------------- */
//   // const filteredSearch = useMemo(() => {
//   //   const s = searchRetailer.toLowerCase();

//   //   return retailers.filter((r) => {
//   //     return (
//   //       r.business_name?.toLowerCase().includes(s) ||
//   //       r.city?.toLowerCase().includes(s) ||
//   //       r.state?.toLowerCase().includes(s) ||
//   //       r.gst?.toLowerCase().includes(s) ||
//   //       r.contact_phone?.toLowerCase().includes(s)
//   //     );
//   //   });
//   // }, [searchRetailer, retailers]);

//   /* ---------------- PAGINATED DATA ---------------- */
//   // const totalPages = Math.ceil(retailers.length / pageSize);

//   // const paginatedRetailers = retailers.slice(
//   //   (currentPage - 1) * pageSize,
//   //   currentPage * pageSize
//   // );

//   /* ---------------- UI ---------------- */
//   return (
//     <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
//       <div className="lg:col-span-4 space-y-3">

//         <div className="bg-white">
//           {showTabs && (
//           <div className="flex gap-2 mb-3">
//             <button
//               onClick={() => setActiveTab("ARP")}
//               className={`px-3 py-1 text-sm rounded-lg font-semibold transition ${
//                 activeTab === "ARP"
//                   ? "bg-blue-600 text-white"
//                   : "bg-blue-50 text-blue-700 hover:bg-blue-100"
//               }`}
//             >
//               ARP
//             </button>

//             <button
//               onClick={() => setActiveTab("CP")}
//               className={`px-3 py-1 text-sm rounded-lg font-semibold transition ${
//                 activeTab === "CP"
//                   ? "bg-blue-600 text-white"
//                   : "bg-blue-50 text-blue-700 hover:bg-blue-100"
//               }`}
//             >
//               CP
//             </button>
//           </div>
//         )}
//         </div>

//         <SectionCard
//           title={showTabs && activeTab === "CP" ? "CP" : "ARP"}
//         >
          
//             <div className="flex justify-end gap-2 mb-3">
//               {/* <input
//                 type="text"
//                 placeholder="Search by name, city, GST, phone..."
//                 value={searchRetailer}
//                 onChange={(e) => setSearchRetailer(e.target.value)}
//                 className="text-sm border border-slate-200 rounded-lg px-2 py-1 w-48 md:w-64"
//               /> */}

//                 <select
//                   value={searchField}
//                   onChange={(e) => setSearchField(e.target.value)}
//                   className="text-sm border border-slate-200 rounded-lg px-1 py-1"
//                 >
//                   <option value="business_name">Business Name</option>
//                   <option value="company_code">Code</option>
//                   <option value="city">City</option>
//                   <option value="state">State</option>
//                   <option value="district">District</option>
//                   <option value="pincode">Pincode</option>
//                 </select>

//                 {/* SEARCH INPUT */}
//                 <input
//                   type="text"
//                   placeholder={`Search by ${searchField.replace("_", " ")}`}
//                   value={searchValue}
//                   onChange={(e) => setSearchValue(e.target.value)}
//                   className="text-sm border border-slate-200 rounded-lg px-2 py-1 w-40 md:w-56"
//                 />

//                 <div className="hidden lg:block">
//                   <RefreshButton onClick={fetchRetailers} />
//                 </div>
                
//               </div>
    
//           {loading ? (
//             <RegularLoader />
//           ) : (
//             <>

//             {/* Mobile Card Layout */}
//               <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
//                 {retailers.map((r) => (
//                   <div
//                     key={r.id}
//                     onClick={() => setSelectedRetailerId(r.id)}
//                     className="bg-white rounded-2xl border border-slate-200 shadow-[0_4px_14px_rgba(0,0,0,0.05)] p-4 active:scale-[0.99] transition-all duration-200"
//                   >
//                     {/* Top Section */}
//                     <div className="flex justify-between items-start">
//                       <div className="flex flex-col">
//                         <h3 className="text-sm font-semibold text-slate-800 leading-tight">
//                           {r.business_name}
//                         </h3>
//                         <span className="text-xs text-slate-400 mt-0.5">
//                           {r.company_code}
//                         </span>

//                         <a
//                             href={`tel:${r.contact_phone}`}
//                             onClick={(e) => e.stopPropagation()}
//                             className="text-xs font-medium text-blue-600"
//                           >
//                             {r.contact_phone}
//                           </a>
//                       </div>

//                       <Badge color={r.status === 1 ? "green" : "red"}>
//                         {r.status === 1 ? "Active" : "Inactive"}
//                       </Badge>
//                     </div>

//                     {/* Divider */}
//                     <div className="my-3 h-px bg-slate-100" />

//                     {/* Info Grid */}
//                     <div className="grid grid-cols-2 gap-y-3 text-xs">
//                       <div>
//                         <p className="text-slate-400">Location</p>
//                         <p className="text-slate-700 font-medium">
//                           {r.district}, {r.state}
//                         </p>
//                       </div>

//                       <div>
//                         <p className="text-slate-400">Pincode</p>
//                         <p className="text-slate-700 font-medium">
//                           {r.pincode || "-"}
//                         </p>
//                       </div>

//                       <div>
//                         <p className="text-slate-400">Verification</p>
//                         <Badge color={r.is_verified === 7 ? "blue" : "gray"}>
//                           {r.is_verified === 7 ? "On" : "Pending"}
//                         </Badge>
//                       </div>

//                       <div>
//                         <p className="text-slate-400">Created By</p>
//                         {r.created_by_name || "-"}
//                       </div>
//                     </div>

//                     {/* Contact Section */}
//                     <div className="mt-4 flex items-center justify-end">
                    
//                       {/* <p className="mt-0.5 text-sm text-slate-500">
                        
//                       </p> */}

//                       <button
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           navigate(`/user/${r.id}`, {
//                             state: {
//                               entityType:
//                                 activeTab === "CP" ? "agent" : "retailer",
//                               data: r,
//                             },
//                           });
//                         }}
//                         className="px-4 py-1.5 text-xs font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-700 transition"
//                       >
//                         View
//                       </button>
//                     </div>
//                   </div>
//                 ))}

//                 {retailers.length === 0 && (
//                   <div className="text-center text-sm text-slate-500 py-6">
//                     No user found.
//                   </div>
//                 )}
//               </div>

//               <div className="hidden lg:block overflow-x-auto">
//                 <table className="min-w-full text-sm">
//                   <thead>
//                     <tr className="border-b border-slate-100 text-slate-500">
//                       <th className="py-2 text-left">Business Name</th>
//                       <th className="py-2 text-left">City</th>
//                       <th className="py-2 text-left">Status</th>
//                       <th className="py-2 text-left">Verified</th>
//                       <th className="py-2 text-left">Phone</th>
//                       <th className="py-2 text-left">Created By</th>
//                       <th className="py-2 text-right">Action</th>
//                     </tr>
//                   </thead>

//                   <tbody>
//                     {retailers.map((r) => (
//                       <tr
//                         key={r.id}
//                         onClick={() => setSelectedRetailerId(r.id)}
//                         className="border-b border-slate-50 cursor-pointer hover:bg-blue-50/60"
//                       >
//                         <td className="py-2">
                          
//                           <div className="font-medium text-slate-800">
//                             {r.business_name} ({r.company_code})
//                           </div>
//                           <div className="text-xs text-slate-500">
//                             {r.district}, {r.state}, {r.pincode}
//                           </div>
//                         </td>

//                         <td className="py-2 text-sm text-slate-600">
//                           {r.district}
//                         </td>

//                         <td className="py-2">
//                           <Badge color={r.status === 1 ? "green" : "red"}>
//                             {r.status === 1 ? "Active" : "Inactive"}
//                           </Badge>
//                         </td>

//                         <td className="py-2">
//                           <Badge color={r.is_verified === 7 ? "blue" : "gray"}>
//                             {r.is_verified === 7 ? "Onboarded" : "In Progress"}
//                           </Badge>
//                         </td>

//                         <td className="py-2 text-sm text-blue-700">
//                           <a href={`tel:${r.contact_phone}`}>
//                             📞 {r.contact_phone}
//                           </a>
//                         </td>

//                          <td className="py-2 text-sm text-slate-600">
//                             {r.created_by_id === authUser?.id ? (
//                               <Badge color="green">Self</Badge>
//                             ) : r.agent_id !== null ? (
//                               <Badge color="blue">By Agent</Badge>
//                             ) : (
//                               <Badge color="gray">Other</Badge> // optional fallback
//                             )}

//                             <p className="mt-0.5 text-sm text-slate-500">
//                               {r.created_by_name || "-"}
//                             </p>
//                           </td>


//                         <td
//                           className="py-2 text-right"
//                           onClick={(e) => e.stopPropagation()}
//                         >
//                           <button
//                             onClick={() =>
//                               navigate(`/user/${r.id}`, {
//                                 state: {
//                                   entityType: activeTab === "agent" ? "agent" : "retailer",
//                                   data: r,
//                                 },
//                               })
//                             }
//                             className="px-2 py-1 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
//                           >
//                             View
//                           </button>

//                         </td>
//                       </tr>
//                     ))}

//                     {retailers.length === 0 && (
//                       <tr>
//                         <td
//                           colSpan={6}
//                           className="py-4 text-center text-sm text-slate-500"
//                         >
//                           No user found.
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </table>
//               </div>

//               {pagination.last_page > 1 && (
//                 <div className="mt-3">
//                   <Pagination
//                     currentPage={currentPage}
//                     totalPages={pagination.last_page}
//                     onPageChange={setCurrentPage}
//                   />
//                 </div>
//               )}
//             </>
//           )}
//         </SectionCard>
//       </div>
//     </div>
//   );
// }


// import { useNavigate } from "react-router-dom";
// import { useState, useMemo, useEffect } from "react";
// import { Badge } from "../../../Component/OperationComponents/Badge";
// import { SectionCard } from "../../../Component/OperationComponents/SectionCard";
// import Pagination from "../../../Component/Common/Pagination";
// import RetailerDetailsPanel from "./RetailerDetails";
// import RefreshButton from "../../../Component/Common/RefreshButton";

// export default function OprRetailersView({}) {
//   const navigate = useNavigate();

//   // ********* MOCK RETAILERS (your provided data) *********
//   const mockRetailers = [
//     {
//       id: 1,
//       name: "Shree Mobile Store",
//       code: "RT001",
//       city: "Pune",
//       isActive: true,
//       isConnected: true,
//       usingProduct: true,
//       phone: "+91 9876543210",
//       email: "owner@shreemobile.in",
//       contactPerson: "Rahul Patil",
//       gstin: "27ABCDE1234F1Z5",
//       address: "Shop 12, Main Market, Pune",
//       totalSales: 450000,
//       monthSales: 75000,
//       outstanding: 15000,
//       lastPaymentDate: "2025-11-01",
//       productMix: [
//         { product: "POS License", value: 25000 },
//         { product: "Recharge API", value: 30000 },
//         { product: "DMT", value: 20000 },
//       ],
//     },
//     {
//       id: 2,
//       name: "Sai Telecom",
//       code: "RT002",
//       city: "Mumbai",
//       isActive: true,
//       isConnected: false,
//       usingProduct: false,
//       phone: "+91 9822001122",
//       email: "sai@telecom.com",
//       contactPerson: "Sanjay More",
//       gstin: "27PQRSX6789L1Z8",
//       address: "G-5, Station Road, Mumbai",
//       totalSales: 120000,
//       monthSales: 12000,
//       outstanding: 5000,
//       lastPaymentDate: "2025-10-25",
//       productMix: [
//         { product: "POS License", value: 60000 },
//         { product: "Recharge API", value: 30000 },
//         { product: "AEPS", value: 30000 },
//       ],
//     },
//     {
//       id: 3,
//       name: "Om Electronics",
//       code: "RT003",
//       city: "Nashik",
//       isActive: false,
//       isConnected: false,
//       usingProduct: false,
//       phone: "+91 9988776655",
//       email: "info@omelectronics.in",
//       contactPerson: "Prakash Jadhav",
//       gstin: "27XYZAB1357K1Z9",
//       address: "Shop 3, City Center, Nashik",
//       totalSales: 80000,
//       monthSales: 0,
//       outstanding: 0,
//       lastPaymentDate: "2025-08-10",
//       productMix: [
//         { product: "POS License", value: 30000 },
//         { product: "Recharge API", value: 20000 },
//         { product: "DMT", value: 30000 },
//       ],
//     },
//   ];

//   // ******** STATES ********
//   const [searchRetailer, setSearchRetailer] = useState("");
//   const [selectedRetailerId, setSelectedRetailerId] = useState(null);
//   const selectedRetailer = mockRetailers.find((r) => r.id === selectedRetailerId);

//   // ******** PAGINATION ********
//   const [currentPage, setCurrentPage] = useState(1);
//   const pageSize = 2;

//   useEffect(() => {
//     setCurrentPage(1);
//   }, [searchRetailer]);

//   // ******** SEARCH LOGIC ********
//   const filteredSearch = useMemo(() => {
//     const s = searchRetailer.toLowerCase();

//     return mockRetailers.filter((r) => {
//       return (
//         r.name.toLowerCase().includes(s) ||
//         r.city.toLowerCase().includes(s) ||
//         r.code.toLowerCase().includes(s) ||
//         r.gstin.toLowerCase().includes(s) ||
//         r.phone.toLowerCase().includes(s)
//       );
//     });
//   }, [searchRetailer]);

//   // ******** PAGINATED DATA ********
//   const totalPages = Math.ceil(filteredSearch.length / pageSize);

//   const paginatedRetailers = filteredSearch.slice(
//     (currentPage - 1) * pageSize,
//     currentPage * pageSize
//   );

//   return (
//     <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

//       {/* List */}
//       <div className="lg:col-span-4 space-y-3">
//         <SectionCard
//           title="Retailers"
//           right={
//             <div className="flex gap-2">
//             <input
//               type="text"
//               placeholder="Search by name, code, city, GSTIN..."
//               value={searchRetailer}
//               onChange={(e) => setSearchRetailer(e.target.value)}
//               className="text-sm border border-slate-200 rounded-lg px-2 py-1 w-48 md:w-64"
//             />
//             <RefreshButton/>
//             </div>
//           }
          
//         >
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-sm">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500">
//                   <th className="py-2 text-left">Retailer</th>
//                   <th className="py-2 text-left">City</th>
//                   <th className="py-2 text-left">Status</th>
//                   <th className="py-2 text-left">Using</th>
//                   <th className="py-2 text-left">Connected</th>
//                   <th className="py-2 text-left">Phone</th>
//                   <th className="py-2 text-right">Monthly Sales</th>
//                   <th className="py-2 text-right">Action</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {paginatedRetailers.map((r) => (
//                   <tr
//                     key={r.id}
//                     onClick={() => setSelectedRetailerId(r.id)}
//                     className={`border-b border-slate-50 cursor-pointer hover:bg-blue-50/60 ${
//                       selectedRetailerId === r.id ? "bg-blue-50" : "bg-white"
//                     }`}
//                   >
//                     <td className="py-2">
//                       <div className="font-medium text-slate-800">{r.name}</div>
//                       <div className="text-sm text-slate-500">
//                         {r.code} · GSTIN: {r.gstin}
//                       </div>
//                     </td>

//                     <td className="py-2 text-sm text-slate-600">{r.city}</td>

//                     <td className="py-2">
//                       <Badge color={r.isActive ? "green" : "red"}>
//                         {r.isActive ? "Active" : "Inactive"}
//                       </Badge>
//                     </td>

//                     <td className="py-2">
//                       <Badge color={r.usingProduct ? "blue" : "gray"}>
//                         {r.usingProduct ? "Using" : "Not using"}
//                       </Badge>
//                     </td>

//                     <td className="py-2">
//                       <Badge color={r.isConnected ? "green" : "red"}>
//                         {r.isConnected ? "Connected" : "Not Connected"}
//                       </Badge>
//                     </td>

//                     <td className="py-2 text-sm text-blue-700">
//                       <a href={`tel:${r.phone.replace(/\s/g, "")}`}>
//                         📞 {r.phone}
//                       </a>
//                     </td>

//                     <td className="py-2 text-sm text-right text-slate-700">
//                       ₹{(r.monthSales / 1000).toFixed(1)}k
//                     </td>

//                     {/* VIEW BUTTON */}
//                     <td
//                       className="py-2 text-right"
//                       onClick={(e) => e.stopPropagation()}
//                     >
//                       <button
//                         onClick={() =>
//                           navigate(`/retailer/${r.id}`, {
//                             state: { retailer: r },
//                           })
//                         }
//                         className="px-2 py-1 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
//                       >
//                         View
//                       </button>
//                     </td>
//                   </tr>
//                 ))}

//                 {filteredSearch.length === 0 && (
//                   <tr>
//                     <td
//                       colSpan={8}
//                       className="py-4 text-center text-sm text-slate-500"
//                     >
//                       No retailers found.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>

//           {/* Pagination */}
//           {totalPages > 1 && (
//             <div className="mt-3">
//               <Pagination
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={setCurrentPage}
//               />
//             </div>
//           )}
//         </SectionCard>
//       </div>
//     </div>
//   );
// }



// import { useNavigate } from "react-router-dom";
// import { Badge } from "../../../Component/OperationComponents/Badge";
// import { SectionCard } from "../../../Component/OperationComponents/SectionCard";
// import RetailerDetailsPanel from "./RetailerDetails";

// export default function RetailersView({
//   retailers,
//   allRetailers,
//   searchRetailer,
//   setSearchRetailer,
//   selectedRetailer,
//   setSelectedRetailerId,
// }) {

//   const navigate = useNavigate();
//   return (
//     <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
//       {/* List */}
//       <div className="lg:col-span-4 space-y-3">
//         <SectionCard
//           title="Retailers"
//           right={
//             <input
//               type="text"
//               placeholder="Search by name, code, city, GSTIN..."
//               value={searchRetailer}
//               onChange={(e) => setSearchRetailer(e.target.value)}
//               className="text-sm border border-slate-200 rounded-lg px-2 py-1 w-48 md:w-64"
//             />
//           }
//         >
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-sm">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500">
//                   <th className="py-2 text-left">Retailer</th>
//                   <th className="py-2 text-left">City</th>
//                   <th className="py-2 text-left">Status</th>
//                   <th className="py-2 text-left">Using</th>
//                   <th className="py-2 text-left">Connected</th>
//                   <th className="py-2 text-left">Phone</th>
//                   <th className="py-2 text-right">Monthly Sales</th>
//                   <th className="py-2 text-right">Action</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {retailers.map((r) => (
//                   <tr
//                     key={r.id}
//                     onClick={() => setSelectedRetailerId(r.id)}
//                     className={`border-b border-slate-50 cursor-pointer hover:bg-blue-50/60 ${
//                       selectedRetailer && selectedRetailer.id === r.id
//                         ? "bg-blue-50"
//                         : "bg-white"
//                     }`}
//                   >
//                     <td className="py-2">
//                       <div className="font-medium text-slate-800">
//                         {r.name}
//                       </div>
//                       <div className="text-sm text-slate-500">
//                         {r.code} · GSTIN: {r.gstin}
//                       </div>
//                     </td>

//                     <td className="py-2 text-sm text-slate-600">{r.city}</td>

//                     <td className="py-2">
//                       <Badge color={r.isActive ? "green" : "red"}>
//                         {r.isActive ? "Active" : "Inactive"}
//                       </Badge>
//                     </td>

//                     <td className="py-2">
//                       <Badge color={r.usingProduct ? "blue" : "gray"}>
//                         {r.usingProduct ? "Using" : "Not using"}
//                       </Badge>
//                     </td>

//                     <td className="py-2">
//                       <Badge color={r.isConnected ? "green" : "red"}>
//                         {r.isConnected ? "Connected" : "Not Connected"}
//                       </Badge>
//                     </td>

//                     <td className="py-2 text-sm text-blue-700">
//                       <a href={`tel:${r.phone.replace(/\s/g, "")}`}>
//                         📞 {r.phone}
//                       </a>
//                     </td>

//                     <td className="py-2 text-sm text-right text-slate-700">
//                       ₹{(r.monthSales / 1000).toFixed(1)}k
//                     </td>

//                     {/* VIEW BUTTON */}
//                     <td
//                       className="py-2 text-right"
//                       onClick={(e) => e.stopPropagation()} // prevents row click
//                     >
//                       <button
//                         onClick={() =>
//                           navigate(`/retailer/${r.id}`, {
//                             state: {
//                               retailer: r,
//                               allRetailers,
//                             },
//                           })
//                         }
//                         className="px-2 py-1 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
//                       >
//                         View
//                       </button>

//                     </td>
//                   </tr>
//                 ))}

//                 {retailers.length === 0 && (
//                   <tr>
//                     <td
//                       colSpan={8}
//                       className="py-4 text-center text-sm text-slate-500"
//                     >
//                       No retailers found with this search.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </SectionCard>
//       </div>

//       {/* Detail Panel */}
//     </div>
//   );
// }
  